import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import type { OrderRow } from '../lib/api'
import { db } from '../lib/db'
import { clock, dayMonth, money, qtyToText } from '../lib/money'
import { useMediaQuery } from '../lib/hooks'
import { PageBack } from './PageBack'
import { OrderDetailSheet } from './OrderDetailSheet'

/**
 * Danh sách hoá đơn của bộ Modern: bảng bên trái, chi tiết đơn bên phải.
 *
 * Trạng thái ở đây là ba trạng thái <b>có thật</b> của đơn hàng trong app này — đang bán,
 * đã thu tiền, đã huỷ. Bản thiết kế có thêm "chờ xử lý" và "đang làm", nhưng đó là quy
 * trình bếp, thứ sản phẩm này cố ý không làm: quán 10 bàn không có ai đứng điều phối
 * phiếu bếp, và một trạng thái không ai bấm là một trạng thái luôn sai.
 */

type Filter = 'all' | 'open' | 'paid' | 'void'

const FILTERS: { id: Filter; label: string; tone: string }[] = [
  { id: 'all', label: 'Tất cả', tone: 'brand' },
  { id: 'open', label: 'Đang bán', tone: 'amber' },
  { id: 'paid', label: 'Đã thu tiền', tone: 'green' },
  { id: 'void', label: 'Đã huỷ', tone: 'clay' },
]

const stateOf = (r: OrderRow): Filter =>
  r.voidedAt || r.status === 'Void' ? 'void' : r.status === 'Open' ? 'open' : 'paid'

const STATE_LABEL: Record<Filter, string> = {
  all: '',
  open: 'Đang bán',
  paid: 'Đã thu tiền',
  void: 'Đã huỷ',
}

interface Props {
  rows: OrderRow[]
  day: string
  onDay: (day: string) => void
  canPickDay: boolean
  query: string
  onQuery: (q: string) => void
  loading: boolean
  offline: boolean
  onVoided: () => void | Promise<void>
}

const todayInput = () => {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export function OrdersBoard({
  rows,
  day,
  onDay,
  canPickDay,
  query,
  onQuery,
  loading,
  offline,
  onVoided,
}: Props) {
  const nav = useNavigate()
  const hasPane = useMediaQuery('(min-width: 1180px)')

  const [filter, setFilter] = useState<Filter>('all')
  const [kind, setKind] = useState<'all' | 'table' | 'away'>('all')
  const [pickedId, setPickedId] = useState<string | null>(null)

  // Ô "Mang về" cũng là một cái bàn, nên phân loại tại bàn / mang đi phải tra tên bàn nào
  // là ô mang về. Tra trên máy: danh mục đã kéo sẵn về rồi, mất mạng vẫn lọc được.
  const awayNames = useLiveQuery(
    async () =>
      new Set(
        (await db.diningTables.filter((t) => t.isTakeaway).toArray()).map((t) => t.name),
      ),
    [],
    new Set<string>(),
  )

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: rows.length, open: 0, paid: 0, void: 0 }
    for (const r of rows) c[stateOf(r)]++
    return c
  }, [rows])

  const shown = useMemo(
    () =>
      rows.filter((r) => {
        if (filter !== 'all' && stateOf(r) !== filter) return false
        if (kind === 'away') return !!r.tableName && awayNames.has(r.tableName)
        if (kind === 'table') return !r.tableName || !awayNames.has(r.tableName)
        return true
      }),
    [rows, filter, kind, awayNames],
  )

  const picked = shown.find((r) => r.id === pickedId) ?? null
  const revenue = shown.filter((r) => stateOf(r) === 'paid').reduce((s, r) => s + r.total, 0)

  function pick(r: OrderRow) {
    // Đơn đang bán thì mở thẳng màn hình gọi món — người bán muốn thêm món, không phải
    // đọc lại tờ hoá đơn chưa chốt.
    if (stateOf(r) === 'open') {
      nav(`/order/${r.id}`)
      return
    }
    setPickedId(r.id)
  }

  return (
    <div className="board orders-board">
      <header className="page-head">
        <PageBack to={'/'} />
        <span className="page-icon is-blue" aria-hidden="true">
          🧾
        </span>
        <div className="page-title">
          <h1>Đơn hàng</h1>
          <p>Quản lý và theo dõi tất cả đơn hàng</p>
        </div>

        <label className="search search-wide">
          <span className="sr-only">Tìm hoá đơn</span>
          <span className="search-glyph" aria-hidden="true">
            ⌕
          </span>
          <input
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Tìm số hoá đơn, tên bàn, số tiền…"
            inputMode="search"
          />
        </label>
      </header>

      <div className="stat-chips">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`stat-chip is-${f.tone}`}
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
          >
            <span className="stat-chip-key">{f.label}</span>
            <b className="stat-chip-val num">{counts[f.id]}</b>
          </button>
        ))}
      </div>

      <div className="orders-split">
        <div className="orders-main">
          <div className="zone-row">
            <div className="chip-row">
              {canPickDay ? (
                <label className="day-pick">
                  <span className="sr-only">Ngày</span>
                  <input
                    type="date"
                    value={day}
                    max={todayInput()}
                    onChange={(e) => onDay(e.target.value)}
                  />
                </label>
              ) : (
                <span className="chip" aria-disabled="true">
                  Hôm nay
                </span>
              )}

              <button
                type="button"
                className="chip"
                aria-pressed={kind === 'all'}
                onClick={() => setKind('all')}
              >
                Tất cả loại
              </button>
              <button
                type="button"
                className="chip"
                aria-pressed={kind === 'table'}
                onClick={() => setKind('table')}
              >
                🪑 Bán tại bàn
              </button>
              <button
                type="button"
                className="chip"
                aria-pressed={kind === 'away'}
                onClick={() => setKind('away')}
              >
                🛵 Mang về
              </button>
            </div>

            <span className="zone-count">
              {loading ? 'Đang tải…' : `${shown.length} đơn · ${money(revenue)}đ`}
            </span>
          </div>

          {offline && (
            <p className="hint">
              Chưa lấy được dữ liệu từ máy chủ — đang xem hoá đơn lưu trên máy này. Đơn do máy
              khác trong quán bán sẽ hiện khi có mạng lại.
            </p>
          )}

          {shown.length === 0 ? (
            <div className="empty">
              <div className="empty-icon" aria-hidden="true">
                🧾
              </div>
              <p>
                {query
                  ? `Không có hoá đơn nào khớp "${query}".`
                  : loading
                    ? 'Đang tải…'
                    : 'Ngày này chưa có đơn nào.'}
              </p>
            </div>
          ) : (
            <div className="order-table" role="table">
              <div className="order-head" role="row">
                <span role="columnheader">Mã đơn</span>
                <span role="columnheader">Thời gian</span>
                <span role="columnheader">Loại</span>
                <span role="columnheader">Tổng tiền</span>
                <span role="columnheader">Trạng thái</span>
              </div>

              {shown.map((r) => {
                const st = stateOf(r)
                const away = !!r.tableName && awayNames.has(r.tableName)

                return (
                  <button
                    key={r.id}
                    type="button"
                    role="row"
                    className={`order-row is-${st}${pickedId === r.id && hasPane ? ' is-picked' : ''}`}
                    onClick={() => pick(r)}
                  >
                    <span className="order-code num" role="cell">
                      {r.code}
                    </span>
                    <span className="order-time" role="cell">
                      <b>{r.paidAt ? clock(r.paidAt) : '—'}</b>
                      <small>{r.paidAt ? dayMonth(r.paidAt) : ''}</small>
                    </span>
                    <span className="order-kind" role="cell">
                      <span className={`kind-pill${away ? ' is-away' : ''}`}>
                        <span aria-hidden="true">{away ? '🛵' : '🪑'}</span>{' '}
                        {r.tableName ?? 'Bán nhanh'}
                      </span>
                    </span>
                    <span className={`order-total num${st === 'void' ? ' struck' : ''}`} role="cell">
                      {money(r.total)}đ
                    </span>
                    <span className="order-state" role="cell">
                      <span className={`state-pill is-${st}`}>{STATE_LABEL[st]}</span>
                      <small>{qtyToText(r.itemCount * 1000)} món</small>
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <aside className="orders-pane" aria-label="Chi tiết đơn">
          {picked && hasPane ? (
            <OrderDetailSheet
              key={picked.id}
              orderId={picked.id}
              variant="pane"
              onClose={() => setPickedId(null)}
              onVoided={async () => {
                setPickedId(null)
                await onVoided()
              }}
            />
          ) : (
            <div className="pane-empty">
              <div className="empty-icon" aria-hidden="true">
                👆
              </div>
              <p>Chạm vào một đơn để xem chi tiết, in lại hoặc huỷ.</p>
            </div>
          )}
        </aside>
      </div>

      {/* Màn hình hẹp: không có khoang bên phải thì chi tiết đơn mở bằng tấm trượt. */}
      {picked && !hasPane && (
        <OrderDetailSheet
          key={picked.id}
          orderId={picked.id}
          onClose={() => setPickedId(null)}
          onVoided={async () => {
            setPickedId(null)
            await onVoided()
          }}
        />
      )}
    </div>
  )
}
